'use client';
import { useCallback, useRef } from 'react';
import { useUser } from '@/components/hook/user';
import Text from '@/components/common/Text/Text';
import Button from '@/components/common/Button/Button';
import Image from 'next/image';
import { Tooltip, TooltipRefProps } from 'react-tooltip';
import { useModal } from '@/components/hook/modal';
import { useEllaAnalytics } from '@/components/hook/analytics';
import { useCloset } from '@/components/hook/closet';

import { USER_STATUS } from '@/types/User';

import Info from 'public/img/icon/info.svg';
import Close from 'public/img/icon/close.svg';

import LoginModal from '@/components/modal/LoginModal/LoginModal';

import analytics from '@/util/analytics';

import './EllaToast.style.scss';
import { usePathname } from 'next/navigation';

type EllaToastProps = {
  onLoginSuccess?: () => void;
};

function EllaToast({ onLoginSuccess }: EllaToastProps) {
  const { value: user } = useUser();
  const userIsActive = user?.status === USER_STATUS.ACTIVE;
  const modalTools = useModal();
  const tooltipRef = useRef<TooltipRefProps>(null);
  const { value: closet } = useCloset();
  const { metadata } = useEllaAnalytics();

  const currentPath = usePathname();
  const isEllaInApp = currentPath === '/ella-in-app';
  const isEllaInStore = currentPath === '/ella-in-store';

  const handleApplyClick = useCallback(() => {
    analytics.track('Ella AI Apply - Clicked', {
      ...metadata
    });
    // The "X" in the tooltip is a bit close to the "Apply" button on smaller screens,
    // and the click goes through the tooltip, so we don't want to accidentally navigate the user
    // to the apply page if they are just trying to close the tooltip.
    if (tooltipRef.current?.isOpen) {
      return;
    }

    window.location.href = '/apply/account';
  }, []);

  const handleLoginClick = useCallback(() => {
    analytics.track('Ella AI Login - Clicked', {
      ...metadata
    });
    modalTools.showModal(
      <LoginModal
        onClose={modalTools.closeModal}
        onSubmitSuccess={async () => {
          modalTools.closeModal();
          if (onLoginSuccess) {
            await onLoginSuccess();
          }
        }}
        initialValues={{
          username: '',
          password: ''
        }}
      />,
      { topClassName: 'bg-gold' }
    );
  }, [modalTools, onLoginSuccess]);

  if (userIsActive && closet.closet?.error?.status !== 402) return null;
  if (isEllaInStore) return null;

  let toastContent: string | JSX.Element;
  let tooltipContent: string;

  const handleAccountClick = (eventName: string) => {
    analytics.track(eventName, {
      ...metadata
    });
  };

  switch (user?.status) {
    case USER_STATUS.FROZEN:
      if (closet.closet?.error?.status === 402) {
        toastContent = (
          <>
            Your payment method is invalid. Visit{' '}
            {isEllaInApp ? (
              'My Account'
            ) : (
              <a
                href='/account'
                className='Link link-s compact underline'
                onClick={() => {
                  handleAccountClick('Ella AI Past Due My Account - Clicked');
                }}
              >
                My Account
              </a>
            )}
            {
              ' to update your billing details and get the full Ella AI\u2122 experience.'
            }
          </>
        );
        tooltipContent =
          "You can continue using Ella AI\u2122 to browse collections, receive fashion advice, and save items to your wishlist. Once payment details are updated, you'll regain full access to checkout and borrow items from your Vivrelle closet.";
        break;
      }
      toastContent = (
        <>
          Your membership is currently frozen.{' '}
          {isEllaInApp ? (
            'Unfreeze your membership'
          ) : (
            <a
              href='/account'
              className='Link link-s compact underline'
              onClick={() => {
                handleAccountClick('Ella AI Unfreeze Membership - Clicked');
              }}
            >
              Unfreeze your membership
            </a>
          )}
          {' to get the full Ella AI\u2122 experience.'}
        </>
      );
      tooltipContent =
        "While frozen, you can still use Ella AI\u2122 to browse styles, receive fashion recommendations, and save items to your wishlist, but you won't be able to check out or add items to your Vivrelle closet until your account is unfrozen.";
      break;
    // This case can only eb reached if the user is past due
    case USER_STATUS.ACTIVE:
      toastContent = (
        <>
          Your payment method is invalid. Visit{' '}
          {isEllaInApp ? (
            'My Account'
          ) : (
            <a
              href='/account'
              className='Link link-s compact underline'
              onClick={() => {
                handleAccountClick('Ella AI Past Due My Account - Clicked');
              }}
            >
              My Account
            </a>
          )}
          {
            ' to update your billing details and get the full Ella AI\u2122 experience.'
          }
        </>
      );
      tooltipContent =
        "You can continue using Ella AI\u2122 to browse collections, receive fashion advice, and save items to your wishlist. Once payment details are updated, you'll regain full access to checkout and borrow items from your Vivrelle closet.";
      break;
    case USER_STATUS.CANCELLED:
      toastContent =
        'Your membership is not active. Email membership@vivrelle.com to reactivate and get the full Ella AI\u2122 experience.';
      tooltipContent =
        'You can still use Ella AI\u2122 to explore our collection, get styling advice, and build your wishlist. Reactivate your membership to unlock full features including checkout and adding items to your Vivrelle closet.';
      break;
    case USER_STATUS.PENDING:
      if (user?.isLocked) {
        toastContent = 'Your membership is not active at this time.';
        tooltipContent =
          "While browsing, you can use Ella AI\u2122 to discover products, receive style suggestions, and add items to your wishlist. To check out or add items to your Vivrelle closet, you'll need to resolve your account status.";
        break;
      }
      toastContent =
        'Your membership is pending approval. Use Ella AI\u2122 to build your wishlist and browse items from REVOLVE and FWRD.';
      tooltipContent =
        "You can continue browsing using Ella AI\u2122 to explore our collection, get style recommendations, and build your wishlist. Once accepted, you'll also be able to borrow items from our closet and check out with REVOLVE and FWRD items directly in the chat.";
      break;
    case USER_STATUS.PENDING_MORE_INFO:
      toastContent =
        'Your membership is pending approval. Use Ella AI\u2122 to build your wishlist and browse items from REVOLVE and FWRD.';
      tooltipContent =
        "You can continue browsing using Ella AI\u2122 to explore our collection, get style recommendations, and build your wishlist. Once accepted, you'll also be able to borrow items from our closet and check out with REVOLVE and FWRD items directly in the chat.";
      break;
    case USER_STATUS.WAITLIST:
      toastContent =
        'Your membership is pending approval. Use Ella AI\u2122 to build your wishlist and browse items from REVOLVE and FWRD.';
      tooltipContent =
        "You can continue browsing using Ella AI\u2122 to explore our collection, get style recommendations, and build your wishlist. Once accepted, you'll also be able to borrow items from our closet and check out with REVOLVE and FWRD items directly in the chat.";
      break;
    case USER_STATUS.DENIED:
      toastContent =
        'Your membership is pending approval. Use Ella AI\u2122 to build your wishlist and browse items from REVOLVE and FWRD.';
      tooltipContent =
        "You can continue browsing using Ella AI\u2122 to explore our collection, get style recommendations, and build your wishlist. Once accepted, you'll also be able to borrow items from our closet and check out with REVOLVE and FWRD items directly in the chat.";
      break;
    default:
      toastContent = 'Login or apply to get the full Ella AI\u2122 experience';
      tooltipContent =
        "While using Ella AI\u2122, you can browse and search the entire collection, get personalized style advice, and add items to your wishlist. To checkout or add items to your Vivrelle closet, you'll need to login or become a member.";
      break;
  }

  return (
    <div className='EllaToast-container'>
      <div className='EllaToast-content'>
        <div className='EllaToast-description'>
          <Text variant='text-s-500'>
            {toastContent}
            <Image
              src={Info}
              alt='Info'
              width={14}
              height={14}
              data-tooltip-id='ella-toast-tooltip'
              className='EllaToast-tooltip-anchor'
            />
          </Text>
          <Tooltip
            id='ella-toast-tooltip'
            place='bottom'
            className='EllaToast-tooltip'
            classNameArrow='EllaToast-tooltip-arrow'
            anchorSelect='.EllaToast-tooltip-anchor'
            opacity={1}
            ref={tooltipRef}
          >
            <Image
              className='close'
              src={Close}
              alt=''
            />
            <Text variant='text-s'>{tooltipContent}</Text>
          </Tooltip>
        </div>
        {!user && (
          <div className='EllaToast-buttons'>
            <Button
              className='EllaToast-button'
              variant='light'
              onClick={handleLoginClick}
            >
              <Text
                className='EllaToast-button-text'
                variant='title-xxs-500'
              >
                Login
              </Text>
            </Button>
            <Button
              className='EllaToast-button'
              variant='light'
              onClick={handleApplyClick}
            >
              <Text
                className='EllaToast-button-text'
                variant='title-xxs-500'
              >
                Apply
              </Text>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default EllaToast;
